import styled, { css } from "styled-components";
import { useState, useRef, useMemo, useEffect } from "react";
import { ChevronRight, ChevronLeft, CalendarDays } from "lucide-react";

import {
  weekdayNames,
  nightsBetween,
  monthGrid,
  isWithin,
  isBefore,
  formatMonth,
  formatLong,
  formatTrigger,
  addMonths,
} from "./dateRange";
import {
  z,
  weight,
  type,
  tracking,
  space,
  shadow,
  radius,
  motion,
  media,
  font,
  color,
} from "../../../styles/tokens";

/* A booking date-range picker.
 *
 * Replaces two <input type="date"> fields. The native control hands the whole job to the operating
 * system: it cannot express a RANGE, so a guest picked two dates with no idea how many nights that
 * was and nothing stopped them choosing a check-out before their check-in. It also renders in the
 * OS locale and format, which on an Indonesian page could show US month/day order.
 *
 * Two months are shown side by side because a stay routinely crosses one, and paging back and forth
 * to compare is the main thing that makes a one-month picker tiring.
 *
 * `today` comes from the server in the property's timezone — a hotel's "today" is the hotel's, and
 * deriving it in the browser would also break hydration for anyone a day ahead.
 */

const Wrap = styled.div`
  position: relative;
`;

const Fields = styled.button`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  gap: ${space[3]};
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${(props) => (props.$open ? color.primary : color.border)};
  border-radius: ${radius.sm};
  background: ${color.canvas};
  text-align: left;
  cursor: pointer;
  transition: border-color ${motion.fast};

  &:hover {
    border-color: ${color.borderStrong};
  }
`;

const Slot = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  small {
    font-size: ${type.xs};
    font-weight: ${weight.semibold};
    letter-spacing: ${tracking.eyebrow};
    text-transform: uppercase;
    color: ${color.ink500};
  }

  strong {
    font-family: ${font.body};
    font-size: ${type.base};
    font-weight: ${weight.medium};
    color: ${(props) => (props.$empty ? color.ink500 : color.ink)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: ${z.overlay};
  padding: ${space[5]};
  border: 1px solid ${color.border};
  border-radius: ${radius.lg};
  background: ${color.surface};
  box-shadow: ${shadow.lg};

  ${media.md`
    left: 50%;
    transform: translateX(-50%);
    width: min(92vw, 360px);
    padding: ${space[4]};
  `}
`;

const Months = styled.div`
  display: flex;
  gap: ${space[6]};

  ${media.md`
    /* One month on a phone: two would each be too narrow to hit accurately. */
    gap: 0;

    > *:last-child {
      display: none;
    }
  `}
`;

const Month = styled.div`
  width: 252px;

  ${media.md`
    width: 100%;
  `}
`;

const MonthHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space[3]};
  font-family: ${font.display};
  font-size: ${type.h4};
  color: ${color.ink};
`;

const Nav = styled.button`
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${color.border};
  border-radius: ${radius.pill};
  background: none;
  color: ${color.ink};
  cursor: pointer;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${color.ink};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const Weekday = styled.span`
  padding-bottom: 6px;
  text-align: center;
  font-size: ${type.xs};
  font-weight: ${weight.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${color.ink500};
`;

/* One button per day. The range fill is a background on the cell rather than a separate layer, so
 * the run of selected nights reads as one continuous band. */
const Day = styled.button`
  position: relative;
  height: 36px;
  border: 0;
  background: none;
  font-family: ${font.body};
  font-size: ${type.sm};
  color: ${color.ink};
  cursor: pointer;

  &:disabled {
    color: ${color.border};
    cursor: not-allowed;
  }

  ${(props) =>
    props.$inRange &&
    css`
      background: ${color.primarySoft};
    `}

  ${(props) =>
    props.$edge &&
    css`
      background: ${color.primary};
      color: ${color.onDark};
      font-weight: ${weight.semibold};
      border-radius: ${props.$edge === "start" ? "4px 0 0 4px" : "0 4px 4px 0"};
    `}

  ${(props) =>
    props.$single &&
    css`
      border-radius: 4px;
    `}

  &:hover:not(:disabled):not([data-edge]) {
    box-shadow: inset 0 0 0 1px ${color.primary};
    border-radius: 4px;
  }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[4]};
  margin-top: ${space[4]};
  padding-top: ${space[4]};
  border-top: 1px solid ${color.border};
  font-size: ${type.sm};
  color: ${color.ink500};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[4]};
`;

const Confirm = styled.button`
  padding: 8px 20px;
  border: 1px solid ${color.primary};
  border-radius: ${radius.sm};
  background: ${color.primary};
  font-family: ${font.body};
  font-size: ${type.sm};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.wide};
  text-transform: uppercase;
  color: ${color.onDark};
  cursor: pointer;
  transition: background ${motion.base};

  &:hover:not(:disabled) {
    background: ${color.primaryHover};
    border-color: ${color.primaryHover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TextButton = styled.button`
  border: 0;
  background: none;
  font-family: ${font.body};
  font-size: ${type.sm};
  font-weight: ${weight.semibold};
  color: ${color.primaryText};
  cursor: pointer;
  text-decoration: underline;
`;

const DateRangePicker = (props) => {
  const { checkIn, checkOut, today, maxNights, language, labels, onChange } =
    props;
  const [open, setOpen] = useState(false);
  // Which month sits on the LEFT. Anchored to the current check-in so reopening returns you there.
  const [anchor, setAnchor] = useState(checkIn || today);
  const [hovered, setHovered] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target))
        setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const weekdays = useMemo(() => weekdayNames(language), [language]);
  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;

  // While only a check-in is set, the row under the cursor previews the stay it would make.
  const previewEnd =
    checkOut || (hovered && isBefore(checkIn, hovered) ? hovered : null);

  const pick = (iso) => {
    if (!checkIn || checkOut || isBefore(iso, checkIn) || iso === checkIn) {
      // Start a new range. A click on or before the current check-in restarts rather than
      // producing a zero- or negative-length stay.
      onChange({ checkIn: iso, checkOut: null });
      return;
    }
    onChange({ checkIn, checkOut: iso });
  };

  const disabledFor = (iso) => {
    if (isBefore(iso, today)) return true;
    // Once a check-in is chosen, nothing beyond the maximum stay is selectable.
    if (
      checkIn &&
      !checkOut &&
      maxNights &&
      nightsBetween(checkIn, iso) > maxNights
    )
      return true;
    return false;
  };

  const renderMonth = (monthAnchor, index) => (
    <Month key={monthAnchor}>
      <MonthHead>
        {index === 0 ? (
          <Nav
            type="button"
            aria-label={labels.prevMonth}
            disabled={!isBefore(today, monthAnchor)}
            onClick={() => setAnchor(addMonths(anchor, -1))}
          >
            <ChevronLeft size={16} />
          </Nav>
        ) : (
          <span />
        )}
        <span>{formatMonth(monthAnchor, language)}</span>
        {index === 1 ? (
          <Nav
            type="button"
            aria-label={labels.nextMonth}
            onClick={() => setAnchor(addMonths(anchor, 1))}
          >
            <ChevronRight size={16} />
          </Nav>
        ) : (
          <span />
        )}
      </MonthHead>

      <Grid role="grid">
        {weekdays.map((name) => (
          <Weekday key={name} role="columnheader" aria-hidden="true">
            {name}
          </Weekday>
        ))}
        {monthGrid(monthAnchor)
          .flat()
          .map((cell) => {
            if (!cell.inMonth) return <span key={cell.iso} />;

            const isStart = cell.iso === checkIn;
            const isEnd = checkOut ? cell.iso === checkOut : false;
            const inRange =
              checkIn && previewEnd && isWithin(cell.iso, checkIn, previewEnd);
            const edge = isStart ? "start" : isEnd ? "end" : null;

            return (
              <Day
                key={cell.iso}
                type="button"
                role="gridcell"
                data-edge={edge || undefined}
                $edge={edge}
                $single={isStart && !checkOut}
                $inRange={Boolean(inRange)}
                aria-label={formatLong(cell.iso, language)}
                aria-selected={Boolean(isStart || isEnd || inRange)}
                disabled={disabledFor(cell.iso)}
                onMouseEnter={() => setHovered(cell.iso)}
                onFocus={() => setHovered(cell.iso)}
                onClick={() => pick(cell.iso)}
              >
                {cell.day}
              </Day>
            );
          })}
      </Grid>
    </Month>
  );

  const monthAnchors = [anchor, addMonths(anchor, 1)];

  return (
    <Wrap ref={rootRef}>
      <Fields
        type="button"
        $open={open}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <Slot $empty={!checkIn}>
          <small>{labels.checkIn}</small>
          <strong>
            {checkIn ? formatTrigger(checkIn, language) : labels.addDates}
          </strong>
        </Slot>
        <Slot $empty={!checkOut}>
          <small>{labels.checkOut}</small>
          <strong>
            {checkOut ? formatTrigger(checkOut, language) : labels.addDates}
          </strong>
        </Slot>
        <CalendarDays size={18} aria-hidden="true" />
      </Fields>

      {open ? (
        <Panel
          role="dialog"
          aria-label={labels.selectDates}
          onMouseLeave={() => setHovered(null)}
        >
          <Months>{monthAnchors.map(renderMonth)}</Months>
          <Foot>
            <span>
              {nights > 0
                ? `${nights} ${nights === 1 ? labels.night : labels.nights}`
                : labels.maxStay.replace("%{n}", maxNights)}
            </span>
            <Actions>
              <TextButton
                type="button"
                onClick={() => onChange({ checkIn: null, checkOut: null })}
              >
                {labels.clearDates}
              </TextButton>
              {/* Disabled until the range is complete: closing on a half-range would leave the
               * search button dead with nothing on screen saying why. */}
              <Confirm
                type="button"
                disabled={!checkIn || !checkOut}
                onClick={() => setOpen(false)}
              >
                {labels.doneDates}
              </Confirm>
            </Actions>
          </Foot>
        </Panel>
      ) : null}
    </Wrap>
  );
};

export default DateRangePicker;
